import { z } from "zod";
/**
 * Community feature toggles. Mirrors the structured shape from PRD §9.3.2 — the
 * only legal contents of `Community.settings`. Unknown keys are rejected at
 * every level (`.strict()`); free text and PII are forbidden by construction
 * because every leaf is a typed scalar.
 *
 * Each top-level group is OPTIONAL on the wire. When a group is absent the
 * caller is signaling "leave this section alone" — the route resolves missing
 * groups against the persisted settings + per-section defaults so the merge
 * is monotonic (no silent reset on partial PATCH).
 *
 * Defaults centralized in `defaultCommunitySettings`.
 */
export const communityDigestSettingsSchema = z
    .object({
    enabled: z.boolean(),
    hourUtc: z.number().int().min(0).max(23),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7),
})
    .strict();
export const communityAlertsSettingsSchema = z
    .object({
    enabled: z.boolean(),
    /**
     * Optional alternate DM target. Telegram chat ids fit in BigInt; on the
     * wire we accept a decimal string to avoid Number precision loss in
     * non-typed clients (the Mini App fetcher returns parsed JSON).
     */
    channelId: z
        .string()
        .regex(/^-?\d+$/)
        .nullable()
        .optional(),
})
    .strict();
export const communityTransparencySettingsSchema = z
    .object({
    autoPin: z.boolean(),
})
    .strict();
export const communitySettingsSchema = z
    .object({
    digest: communityDigestSettingsSchema,
    alerts: communityAlertsSettingsSchema,
    transparency: communityTransparencySettingsSchema,
})
    .strict();
/**
 * Default values for every settings group. Read by the PATCH merger when a
 * caller's body omits a section AND the persisted row has a partial settings
 * blob (e.g. legacy rows that predate this schema). Centralizing the default
 * shape here keeps drift between repository, route and frontend impossible —
 * the same constant is the schema's single source of truth.
 */
export const defaultCommunitySettings = {
    digest: { enabled: false, hourUtc: 9, weekdays: [0, 1, 2, 3, 4, 5, 6] },
    alerts: { enabled: false, channelId: null },
    transparency: { autoPin: true },
};
export const communityChatTypeSchema = z.enum([
    "SUPERGROUP",
    "GROUP",
    "CHANNEL",
]);
export const communityStatusSchema = z.enum([
    "ACTIVE",
    "PAUSED",
    "DISCONNECTED",
    "ERROR",
]);
/**
 * `/api/communities` list + detail response. `telegramId` is serialized as a
 * decimal string for BigInt safety (Telegram chat ids exceed Number's safe-
 * integer range). `connectedAt` / `deletedAt` are ISO 8601 strings on the
 * wire; we don't try to coerce to Date inside Zod because the consumer (the
 * frontend) always re-parses with `new Date(...)` for relative-time display.
 */
export const communityResponseSchema = z.object({
    id: z.string().min(1),
    telegramId: z.string().regex(/^-?\d+$/),
    chatType: communityChatTypeSchema,
    ownerId: z.string().min(1),
    title: z.string().min(1),
    username: z.string().nullable(),
    status: communityStatusSchema,
    memberCountCached: z.number().int().nonnegative(),
    settings: communitySettingsSchema,
    transparencyPosted: z.boolean(),
    connectedAt: z.string().datetime(),
});
/**
 * PATCH /api/communities/:id input. Strict at every level to fail loudly on
 * typos or stale clients sending free-text. `title` is bounded — Telegram
 * chat titles are <=255 chars in practice; we cap at 255 as a defense.
 *
 * Settings sections are individually optional so a caller can update only the
 * digest section without resending alerts/transparency. The PATCH semantics
 * for nested sections are MERGE-PER-SECTION (i.e. if `digest` is present, the
 * entire digest object replaces the persisted one — not field-level merge).
 * This matches the structured-toggle model: each section is small and the
 * frontend always loads the whole community before editing.
 */
export const updateCommunitySettingsSchema = z
    .object({
    digest: communityDigestSettingsSchema.optional(),
    alerts: communityAlertsSettingsSchema.optional(),
    transparency: communityTransparencySettingsSchema.optional(),
})
    .strict();
export const updateCommunityRequestSchema = z
    .object({
    title: z.string().min(1).max(255).optional(),
    settings: updateCommunitySettingsSchema.optional(),
})
    .strict();
/**
 * Telegram MediaType enum mirror — kept in shared so the wire schema for
 * topPosts can typecheck cleanly on both sides without importing Prisma's
 * generated enum into the frontend bundle. MUST match the Prisma enum in
 * packages/backend/prisma/schema.prisma:MediaType byte-for-byte.
 */
export const mediaTypeSchema = z.enum([
    "PHOTO",
    "VIDEO",
    "DOCUMENT",
    "AUDIO",
    "VOICE",
    "STICKER",
    "ANIMATION",
    "OTHER",
]);
/**
 * One row from the latest completed MetricHourly aggregate. Mirrors the
 * `MetricHourly` table columns the Mini App actually renders (Overview hero
 * + 4 metric cards). `engagementScore` may be null for hours where
 * aggregation produced no signal (the per-hour engagement contribution
 * formula returns null for empty hours per TASK-016 documentation).
 */
export const communityOverviewMetricSchema = z.object({
    hourBucket: z.string().datetime(),
    messagesTotal: z.number().int().nonnegative(),
    messagesUniqueSenders: z.number().int().nonnegative(),
    repliesCount: z.number().int().nonnegative(),
    reactionsAdded: z.number().int().nonnegative(),
    reactionsRemoved: z.number().int().nonnegative(),
    activeMembers: z.number().int().nonnegative(),
    newMembers: z.number().int().nonnegative(),
    leftMembers: z.number().int().nonnegative(),
    memberCountEnd: z.number().int().nonnegative(),
    engagementScore: z.number().nullable(),
});
/**
 * One daily aggregate point in the 7-day trend chart. The trend is always
 * exactly 7 entries — missing days are backfilled with zeros so the chart
 * never has a discontinuous x-axis. `date` is a UTC YYYY-MM-DD calendar day.
 */
export const communityOverviewDailyPointSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    messagesTotal: z.number().int().nonnegative(),
    messagesUniqueSenders: z.number().int().nonnegative(),
    activeMembers: z.number().int().nonnegative(),
    newMembers: z.number().int().nonnegative(),
    leftMembers: z.number().int().nonnegative(),
    reactionsAdded: z.number().int().nonnegative(),
});
/**
 * One row in the topPosts list. NEVER includes message text or caption per
 * §15.3 — only metadata (length, media classification, reply/forward flags)
 * and aggregated engagement (reactions added + reply count). `score` is the
 * combined ranking key (`reactionsCount + repliesCount`) — exposed so the
 * frontend can render the underlying signal without re-deriving.
 */
export const communityOverviewTopPostSchema = z.object({
    telegramMessageId: z.string().regex(/^-?\d+$/),
    senderTelegramId: z.string().regex(/^-?\d+$/),
    sentAt: z.string().datetime(),
    contentLength: z.number().int().nonnegative(),
    hasMedia: z.boolean(),
    mediaType: mediaTypeSchema.nullable(),
    isReply: z.boolean(),
    isForwarded: z.boolean(),
    reactionsCount: z.number().int().nonnegative(),
    repliesCount: z.number().int().nonnegative(),
    score: z.number().int().nonnegative(),
});
/**
 * GET /api/communities/:id/overview — TASK-027. Composes
 * `communityResponseSchema` (the Community projection from TASK-026) with a
 * dashboard payload: latest hourly metric, 7-day daily trend, top 5 posts,
 * engagementScore, memberCountCached, 24h net growth.
 *
 * `dataAvailable` is FALSE when the community has < 1h of MetricHourly data
 * (e.g. just connected). In that case `current` is null, `trend` is 7
 * zero-filled days, `topPosts` is [], and the scalar fields are zeros — the
 * Mini App branches on this flag to render its onboarding empty state
 * instead of misleadingly empty cards.
 */
export const communityOverviewResponseSchema = z.object({
    community: communityResponseSchema,
    current: communityOverviewMetricSchema.nullable(),
    trend: z.array(communityOverviewDailyPointSchema).length(7),
    topPosts: z.array(communityOverviewTopPostSchema).max(5),
    engagementScore: z.number().nullable(),
    memberCountCached: z.number().int().nonnegative(),
    netGrowth24h: z.number().int(),
    dataAvailable: z.boolean(),
    generatedAt: z.string().datetime(),
});
//# sourceMappingURL=communities.js.map